import { useEffect, useState } from "react";
import { fetchMentorDetail, fetchMentorDetailInsight, submitMentorRequest, fetchMenteePreparation } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { Calendar, GraduationCap, Target, MessageSquare, CheckCircle2, Clock, ListChecks, Sparkles, HelpCircle, Eye, ListTodo, Quote, Heart, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function MentorDetailDrawer({ matchId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiEnriched, setAiEnriched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [message, setMessage] = useState("");
  const [cadence, setCadence] = useState("iki-haftalik");

  // Mentee preparation pack (loaded after submit)
  const [menteePrep, setMenteePrep] = useState(null);
  const [menteePrepLoading, setMenteePrepLoading] = useState(false);

  useEffect(() => {
    if (!open || !matchId) return;
    let cancelled = false;
    setLoading(true);
    setAiLoading(true);
    setAiEnriched(false);
    setSubmitted(null);
    setMessage("");
    setMenteePrep(null);
    setMenteePrepLoading(false);
    // Fast deterministic detail
    fetchMentorDetail(matchId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // AI roadmap in parallel — overlays content when ready
    fetchMentorDetailInsight(matchId)
      .then((ai) => {
        if (!cancelled && ai && typeof ai === "object") {
          setData((prev) => (prev ? {
            ...prev,
            roadmap: ai.roadmap?.length ? ai.roadmap : prev.roadmap,
            discussion_topics: ai.discussion_topics?.length ? ai.discussion_topics : prev.discussion_topics,
            first_meeting_agenda: ai.first_meeting_agenda?.length ? ai.first_meeting_agenda : prev.first_meeting_agenda,
            meeting_cadence: ai.meeting_cadence || prev.meeting_cadence,
          } : prev));
          setAiEnriched(true);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setAiLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [matchId, open]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const resp = await submitMentorRequest({
        match_id: matchId,
        message,
        proposed_cadence: cadence,
        mentee_id: data?.mentee?.id,
        mentor_id: data?.mentor?.id,
        focus_areas: (data?.focus_areas || []).map((f) => f.id || f.name),
      });
      setSubmitted(resp);
      toast.success("Mentorluk talebin gönderildi", {
        description: resp.next_step,
      });
      // Auto-fetch mentee preparation pack for the next meeting
      if (resp.request_id) {
        setMenteePrepLoading(true);
        fetchMenteePreparation(resp.request_id)
          .then((p) => setMenteePrep(p))
          .catch(() => setMenteePrep(null))
          .finally(() => setMenteePrepLoading(false));
      }
    } catch (e) {
      toast.error("Talep gönderilemedi", { description: "Lütfen tekrar deneyin." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-white sm:max-w-2xl w-full overflow-y-auto p-0 border-l border-slate-200"
        data-testid="mentor-detail-drawer"
      >
        {loading || !data ? (
          <div className="p-8 space-y-3">
            <div className="h-6 bg-slate-200/60 rounded animate-pulse w-1/2" />
            <div className="h-4 bg-slate-100 rounded animate-pulse w-2/3" />
            <div className="h-32 bg-slate-100 rounded-xl animate-pulse mt-4" />
          </div>
        ) : (
          <>
            {/* Hero header */}
            <div className="px-8 pt-8 pb-6 bg-gradient-to-br from-sky-50 via-white to-white border-b border-slate-200">
              <SheetHeader className="text-left">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-sky-500/20 border border-sky-500/40 flex items-center justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-sky-700" />
                  </div>
                  <span className="label-eyebrow text-sky-700">Mentor Eşleşme Detayı</span>
                </div>
                <div className="flex items-start gap-5">
                  <Avatar className="w-20 h-20 ring-2 ring-sky-500/30">
                    <AvatarImage src={data.mentor.avatar} />
                    <AvatarFallback>{data.mentor.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <SheetTitle className="font-display text-2xl font-semibold tracking-tight">
                      {data.mentor.name}
                    </SheetTitle>
                    <SheetDescription className="text-sm text-slate-600 mt-1">
                      {data.mentor.title} · {data.mentor.department}
                      {data.mentor.tenure_years ? ` · ${data.mentor.tenure_years} yıl` : ""}
                    </SheetDescription>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-3xl font-semibold text-sky-700">%{data.match_score}</div>
                    <div className="text-[0.65rem] text-slate-500 uppercase tracking-wider">uyum</div>
                  </div>
                </div>
              </SheetHeader>
            </div>

            <div className="px-8 py-6 space-y-7">
              {/* Focus areas */}
              {data.focus_areas?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-4 h-4 text-sky-600" />
                    <div className="label-eyebrow">Gelişim Odakları</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {data.focus_areas.map((f) => (
                      <span key={f.id} className="px-3 py-1.5 rounded-full text-xs border border-sky-200 bg-sky-50 text-sky-800">
                        {f.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Rationale */}
              {data.rationale?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <ListChecks className="w-4 h-4 text-sky-600" />
                    <div className="label-eyebrow">Neden Bu Eşleşme?</div>
                  </div>
                  <div className="space-y-2.5">
                    {data.rationale.map((r, i) => (
                      <div key={i} className="rounded-lg border border-slate-200 p-3.5 bg-slate-50/60">
                        <div className="flex items-start justify-between gap-3 mb-1.5">
                          <div className="text-sm font-medium text-slate-900">{r.kriter}</div>
                          {typeof r.skor === "number" && (
                            <Badge variant="outline" className="border-sky-500/40 text-sky-700 bg-sky-500/10 font-mono text-[0.7rem]">
                              %{r.skor}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-600 leading-relaxed">{r.aciklama}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Roadmap */}
              {data.roadmap?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4 text-sky-600" />
                    <div className="label-eyebrow">{data.recommended_months || 6} Aylık Yol Haritası</div>
                    {aiLoading && (
                      <span className="text-[0.6rem] text-sky-700 bg-sky-50 border border-sky-200 px-1.5 py-0.5 rounded-full ai-pulse">
                        AI zenginleştiriyor…
                      </span>
                    )}
                    {aiEnriched && (
                      <span className="text-[0.6rem] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1">
                        <Sparkles className="w-2.5 h-2.5" /> Kişiselleştirildi
                      </span>
                    )}
                  </div>
                  <div className="space-y-2.5" data-testid="mentor-roadmap">
                    {data.roadmap.map((m, idx) => (
                      <div key={idx} className="relative pl-10">
                        <div className="absolute left-0 top-1 w-7 h-7 rounded-full bg-sky-100 border-2 border-sky-300 text-sky-800 font-mono text-xs font-bold flex items-center justify-center">
                          {m.month}
                        </div>
                        <div className="rounded-lg border border-slate-200 p-3.5 bg-white">
                          <div className="text-sm font-semibold text-slate-900 mb-1">{m.theme}</div>
                          {m.milestones?.length > 0 && (
                            <ul className="text-xs text-slate-600 space-y-1 mb-2">
                              {m.milestones.map((ms, j) => (
                                <li key={j} className="flex items-start gap-1.5">
                                  <CheckCircle2 className="w-3 h-3 text-sky-500 mt-0.5 flex-shrink-0" />
                                  <span>{ms}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                          {m.outcome && (
                            <div className="text-[0.7rem] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 inline-block">
                              → {m.outcome}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* First meeting agenda */}
              {data.first_meeting_agenda?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="w-4 h-4 text-sky-600" />
                    <div className="label-eyebrow">İlk Görüşme Gündemi</div>
                  </div>
                  <ol className="space-y-1.5 text-sm text-slate-800">
                    {data.first_meeting_agenda.map((a, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="font-mono text-xs text-sky-700 font-semibold w-5 flex-shrink-0">{i + 1}.</span>
                        <span>{a}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Discussion topics */}
              {data.discussion_topics?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <GraduationCap className="w-4 h-4 text-sky-600" />
                    <div className="label-eyebrow">Tartışma Konuları</div>
                  </div>
                  <div className="grid grid-cols-1 gap-1.5">
                    {data.discussion_topics.map((t, i) => (
                      <div key={i} className="text-sm text-slate-700 flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-sky-400 mt-2 flex-shrink-0" />
                        <span>{t}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Meeting cadence */}
              {data.meeting_cadence && (
                <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
                  <div className="flex items-start gap-3">
                    <Clock className="w-4 h-4 text-sky-700 mt-0.5" />
                    <div>
                      <div className="text-xs font-medium text-sky-900 uppercase tracking-wider mb-1">
                        Önerilen Görüşme Kadansı
                      </div>
                      <p className="text-sm text-slate-800">{data.meeting_cadence}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Request form */}
              <div className="border-t border-slate-200 pt-6">
                {submitted ? (
                  <div className="space-y-5" data-testid="mentor-request-success">
                    <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-5">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="font-semibold text-emerald-900 mb-1">Talep gönderildi</div>
                          <p className="text-sm text-emerald-800 mb-2">{submitted.next_step}</p>
                          <div className="text-[0.65rem] text-emerald-700 font-mono">Talep no: {submitted.request_id}</div>
                        </div>
                      </div>
                    </div>

                    {/* Mentee Preparation Pack — AI-generated, shown to mentee while waiting for approval */}
                    <div
                      className="rounded-xl border-2 border-violet-200 bg-gradient-to-br from-violet-50/80 via-white to-white p-5"
                      data-testid="mentee-prep-pack"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-violet-500/20 border border-violet-400/50 flex items-center justify-center">
                            <Sparkles className="w-3.5 h-3.5 text-violet-700" />
                          </div>
                          <div>
                            <div className="text-[0.65rem] uppercase tracking-wider text-violet-700 font-semibold">
                              Görüşmene Hazırlık
                            </div>
                            <div className="text-xs text-slate-500">
                              {data?.mentor?.name?.split(" ")[0]} onayını bekliyor — sen de bu arada hazırlan
                            </div>
                          </div>
                        </div>
                        {menteePrep && !menteePrepLoading && submitted.request_id && (
                          <button
                            type="button"
                            data-testid="mentee-prep-refresh"
                            onClick={async () => {
                              setMenteePrepLoading(true);
                              setMenteePrep(null);
                              try {
                                const p = await fetchMenteePreparation(submitted.request_id, true);
                                setMenteePrep(p);
                              } finally {
                                setMenteePrepLoading(false);
                              }
                            }}
                            className="text-[0.65rem] text-violet-700 hover:underline inline-flex items-center gap-1"
                          >
                            <RefreshCw className="w-3 h-3" /> Yenile
                          </button>
                        )}
                      </div>

                      {menteePrepLoading && (
                        <div className="space-y-2.5" data-testid="mentee-prep-loading">
                          <div className="h-3 bg-slate-200/60 rounded animate-pulse w-full" />
                          <div className="h-3 bg-slate-200/60 rounded animate-pulse w-5/6" />
                          <div className="h-3 bg-slate-200/60 rounded animate-pulse w-3/4" />
                          <div className="text-[0.65rem] text-slate-500 italic mt-2">
                            {`Plena AI ${data?.mentor?.name?.split(" ")[0]} ile görüşmen için hazırlık yapıyor…`}
                          </div>
                        </div>
                      )}

                      {menteePrep && !menteePrepLoading && (
                        <div className="space-y-4">
                          {menteePrep.mentor_snapshot && (
                            <div>
                              <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                                <Target className="w-3 h-3 text-violet-600" /> Mentorun Hakkında
                              </div>
                              <p className="text-sm text-slate-800 leading-relaxed">{menteePrep.mentor_snapshot}</p>
                            </div>
                          )}

                          {menteePrep.things_to_ask?.length > 0 && (
                            <div>
                              <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                                <HelpCircle className="w-3 h-3 text-violet-600" /> Sorabileceğin Sorular
                              </div>
                              <ol className="space-y-1.5">
                                {menteePrep.things_to_ask.map((q, i) => (
                                  <li key={i} className="text-sm text-slate-800 flex gap-2">
                                    <span className="font-mono text-xs text-violet-700 font-semibold w-5 flex-shrink-0">{i + 1}.</span>
                                    <span className="leading-snug">{q}</span>
                                  </li>
                                ))}
                              </ol>
                            </div>
                          )}

                          {menteePrep.things_to_expect?.length > 0 && (
                            <div>
                              <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                                <Eye className="w-3 h-3 text-violet-600" /> Ne Bekleyebilirsin
                              </div>
                              <ul className="space-y-1">
                                {menteePrep.things_to_expect.map((e, i) => (
                                  <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-violet-400 mt-2 flex-shrink-0" />
                                    <span>{e}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {menteePrep.prep_actions?.length > 0 && (
                            <div>
                              <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                                <ListTodo className="w-3 h-3 text-violet-600" /> Görüşmeden Önce Yap
                              </div>
                              <ul className="space-y-1">
                                {menteePrep.prep_actions.map((a, i) => (
                                  <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-violet-500 mt-0.5 flex-shrink-0" />
                                    <span>{a}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {menteePrep.first_5_min_opener && (
                            <div className="rounded-lg bg-violet-100/60 border border-violet-300 px-3 py-3">
                              <div className="text-[0.65rem] uppercase tracking-wider text-violet-800 mb-1.5 flex items-center gap-1.5">
                                <Quote className="w-3 h-3" /> İlk 5 Dakika İçin Açılış Önerisi
                              </div>
                              <p className="text-sm text-violet-900 leading-relaxed italic">
                                {menteePrep.first_5_min_opener}
                              </p>
                            </div>
                          )}

                          {menteePrep.confidence_note && (
                            <div className="flex items-start gap-2 text-xs text-slate-600 italic pt-1">
                              <Heart className="w-3.5 h-3.5 text-rose-500 flex-shrink-0 mt-0.5" />
                              <span>{menteePrep.confidence_note}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="label-eyebrow mb-3 text-sky-700">Mentorluk Talebi Gönder</div>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-xs text-slate-600 mb-2 block">Görüşme sıklığı</Label>
                        <RadioGroup value={cadence} onValueChange={setCadence} className="grid grid-cols-3 gap-2">
                          {[
                            { v: "haftalik", l: "Haftalık" },
                            { v: "iki-haftalik", l: "İki haftada bir" },
                            { v: "aylik", l: "Aylık" },
                          ].map((opt) => (
                            <label
                              key={opt.v}
                              htmlFor={`cad-${opt.v}`}
                              className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition ${
                                cadence === opt.v
                                  ? "border-sky-500 bg-sky-50 text-sky-900"
                                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                              }`}
                            >
                              <RadioGroupItem value={opt.v} id={`cad-${opt.v}`} />
                              <span className="text-sm">{opt.l}</span>
                            </label>
                          ))}
                        </RadioGroup>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600 mb-2 block">Mesajın (opsiyonel)</Label>
                        <Textarea
                          data-testid="mentor-request-message"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder="Mentorundan ne öğrenmek istediğini kısaca yaz…"
                          rows={3}
                          className="bg-white border-slate-200 resize-none"
                        />
                      </div>
                      <Button
                        data-testid="mentor-request-submit-btn"
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                      >
                        <GraduationCap className="w-4 h-4 mr-2" />
                        {submitting ? "Gönderiliyor…" : "Talep gönder"}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
